import React, { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import AlertBox from '../components/AlertBox';
import './Pages.css';

function AdminPage() {
  const { getAccessTokenSilently, isLoading: authLoading, user } = useAuth0();
  const [organizations, setOrganizations] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [operationType, setOperationType] = useState<string | null>(null);
  const [confirmationButtonType, setConfirmationButtonType] = useState(false);
  const [alertType, setAlertType] = useState<'alert' | 'error'>('alert');
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!authLoading && user) {
        setAdminCheckLoading(true);
        try {
          const token = await getAccessTokenSilently();
          const res = await fetch(`/api/admin/check`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
          });

          if (res.ok) {
            const data = await res.json();
            setIsAdmin(data.isAdmin);
          } else {
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        } finally {
          setAdminCheckLoading(false);
        }
      } else if (!authLoading && !user) {
        setAdminCheckLoading(false);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [authLoading, user, getAccessTokenSilently]);

  // Easter egg: Listen for "M" key press
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only trigger if not typing in an input field
      const target = event.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
      
      if (!isInputField && event.key.toLowerCase() === 'm') {
        setShowEasterEgg(true);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  // Fetch all organizations (admin view)
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!isAdmin) return;
      
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`/api/admin/orgs`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
        });

        const isJson = res.headers.get('content-type')?.includes('application/json');
        const responseBody = isJson ? await res.json() : null;

        if (!res.ok) {
          const message =
            responseBody?.error ||
            responseBody?.fullError?.name ||
            res.statusText ||
            'Unknown error';
          throw new Error(`${message}`);
        }

        setOrganizations(responseBody);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setAlertMessage(error instanceof Error ? error.message : 'Unknown error occurred');
        setConfirmationButtonType(true);
        setAlertType('error');
        setShowAlert(true);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && isAdmin) {
      fetchOrganizations();
    }
  }, [getAccessTokenSilently, authLoading, isAdmin]);

  const handleDeleteConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDeleteConfirmation(value);
    setIsDeleteConfirmed(value === 'Delete');
  };

  const handleAlertYes = async () => {
    if (operationType === 'delete' && !isDeleteConfirmed) {
      setAlertMessage('Incorrect confirmation. Please type "Delete" to proceed.');
      setAlertType('error');
      setShowAlert(true);
      return;
    }

    setShowAlert(false);
    setAlertMessage(null);
    setConfirmationButtonType(true);
    setAlertType('alert');

    const orgId = selectedOrgId;
    if (!orgId) {
      setAlertMessage('No organization selected for operation.');
      setShowAlert(true);
      return;
    }

    try {
      let url = '';
      let method = '';
      const token = await getAccessTokenSilently();

      if (operationType === 'delete') {
        url = `/api/admin/orgs/${orgId}`;
        method = 'DELETE';
      } else if (operationType === 'regenerate') {
        url = `/api/admin/orgs/${orgId}/grafana-token`;
        method = 'POST';
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
      });

      const isJson = res.headers.get('content-type')?.includes('application/json');
      const data = isJson ? await res.json() : null;

      if (!res.ok) {
        const message =
          data?.error ||
          data?.fullError?.name ||
          res.statusText ||
          'Unknown error';
        throw new Error(`${message}`);
      }

      if (operationType === 'delete') {
        setOrganizations((prevOrgs: any) => prevOrgs.filter((org: any) => org.id !== orgId));
        setAlertMessage(`Organization deleted successfully!`);
      } else if (operationType === 'regenerate') {
        setAlertMessage(`Token regenerated successfully!\nService Account: ${data.serviceAccount}\nService Token: ${data.serviceToken}`);
      }

      setShowAlert(true);
    } catch (error) {
      console.error('Error performing operation:', error);
      setAlertMessage(error instanceof Error ? error.message : 'Unknown error occurred');
      setConfirmationButtonType(true);
      setAlertType('error');
      setShowAlert(true);
    } finally {
      setSelectedOrgId(null);
      setDeleteConfirmation('');
      setIsDeleteConfirmed(false);
    }
  };

  const handleAlertNo = () => {
    setShowAlert(false);
    setAlertType('alert');
  };

  const handleConfirmation = () => {
    setShowAlert(false);
    setConfirmationButtonType(false);
    setAlertType('alert');
  };

  // Filter and sort organizations based on search term, region, type, and sorting criteria
  const filteredOrganizations = useMemo(() => {
    let filtered = organizations.filter((org: any) => {
      const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           org.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           org.contact_email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = selectedRegion === '' || org.region === selectedRegion;
      const matchesType = selectedType === '' || org.type === selectedType;
      
      return matchesSearch && matchesRegion && matchesType;
    });

    // Sort the filtered results
    filtered.sort((a: any, b: any) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'region':
          aValue = a.region;
          bValue = b.region;
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'code':
          aValue = a.code;
          bValue = b.code;
          break;
        case 'email':
          aValue = a.contact_email.toLowerCase();
          bValue = b.contact_email.toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createdDate);
          bValue = new Date(b.createdDate);
          break;
        case 'metrics':
          aValue = a.observabilityMetrics?.metricsCount || 0;
          bValue = b.observabilityMetrics?.metricsCount || 0;
          break;
        case 'logs':
          aValue = a.observabilityMetrics?.logsCount || 0;
          bValue = b.observabilityMetrics?.logsCount || 0;
          break;
        case 'traces':
          aValue = a.observabilityMetrics?.tracesCount || 0;
          bValue = b.observabilityMetrics?.tracesCount || 0;
          break;
        default:
          return 0;
      }

      // Handle numeric vs string comparison
      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [organizations, searchTerm, selectedRegion, selectedType, sortBy, sortOrder]);

  // Get unique regions and types for filter dropdowns
  const availableRegions = useMemo(() => {
    const regions = Array.from(new Set<string>(organizations.map((org: any) => org.region)));
    return regions.sort();
  }, [organizations]);

  const availableTypes = useMemo(() => {
    const types = Array.from(new Set<string>(organizations.map((org: any) => org.type)));
    return types.sort();
  }, [organizations]);

  // Show loading while checking admin status
  if (authLoading || adminCheckLoading) {
    return (
      <div className="page-container">
        <h1>Loading...</h1>
        <p>Checking administrator privileges...</p>
      </div>
    );
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div className="page-container">
        <h1>Access Denied</h1>
        <p>You do not have administrator privileges to access this page.</p>
        <button 
          className="button-primary"
          onClick={() => navigate('/')}
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1>Administrator Panel</h1>
      <p>Manage all organizations in the system. You have full administrative access.</p>
      
      {/* Search and Filter Controls */}
      <div className="search-filter-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, code, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-controls">
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="filter-select"
          >
            <option value="">All Regions</option>
            {availableRegions.map((region) => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
          
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            {availableTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <div className="sort-controls">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="name">Sort by Name</option>
              <option value="region">Sort by Region</option>
              <option value="type">Sort by Type</option>
              <option value="code">Sort by Code</option>
              <option value="email">Sort by Email</option>
              <option value="created">Sort by Created Date</option>
              <option value="metrics">Sort by Metrics</option>
              <option value="logs">Sort by Logs</option>
              <option value="traces">Sort by Traces</option>
            </select>
            
            <button
              className="sort-order-button"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={`Currently sorting ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          
          {(searchTerm || selectedRegion || selectedType) && (
            <button 
              className="clear-filters-button"
              onClick={() => {
                setSearchTerm('');
                setSelectedRegion('');
                setSelectedType('');
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {loading && <p>Loading...</p>}
      {!loading && organizations.length === 0 && <p>No organizations found.</p>}
      {!loading && organizations.length > 0 && filteredOrganizations.length === 0 && (
        <p>No organizations match your search criteria.</p>
      )}

      {!loading && organizations.length > 0 && (
        <div className="results-info">
          <span>
            Showing {filteredOrganizations.length} of {organizations.length} organizations
          </span>
        </div>
      )}

      <ul className="organization-list">
        {filteredOrganizations.map((org) => (
          <li key={org.id} className="organization-item">
            <div className="organization-details">
              <strong>{org.name}</strong> <br />
              <span>Region:</span> {org.region} <br />
              <span>Type:</span> {org.type} <br />
              <span>X-Scope-OrgId:</span> {org.code} <br />
              <span>Contact Email:</span> {org.contact_email} <br />
              {org.leanix_cost_id && <><span>LeanIX Cost ID:</span> {org.leanix_cost_id} <br /></>}
              <span>Created:</span> {new Date(org.createdDate).toLocaleDateString()} <br />
              <span>Created By:</span> {org.createdBy} <br />
              <span>Modified By:</span> {org.modifiedBy} <br />
              {org.observabilityMetrics && (
                <div className="observability-metrics">
                  <br />
                  <strong>Observability Data:</strong>
                  <div className="metrics-row">
                    <span>Metrics: {org.observabilityMetrics.metricsCount.toLocaleString()}</span>
                    <span>Logs: {org.observabilityMetrics.logsCount.toLocaleString()}</span>
                    <span>Traces: {org.observabilityMetrics.tracesCount.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="admin-actions">
              <button
                className="edit-org-button"
                onClick={() => navigate(`/edit-organization/${org.id}`)}
                title="Edit Organization"
              >
                Edit
              </button>
              <button
                className="delete-org-button"
                onClick={() => {
                  setSelectedOrgId(org.id);
                  setOperationType('delete');
                  setAlertMessage(`Are you sure you want to delete the organization "${org.name}"? This action cannot be undone.`);
                  setConfirmationButtonType(false);
                  setShowAlert(true);
                }}
                title="Delete Organization"
              >
                Delete
              </button>
              <button
                className="regenerate-token-button"
                onClick={() => {
                  setSelectedOrgId(org.id);
                  setOperationType('regenerate');
                  setAlertMessage(`Are you sure you want to regenerate the token for "${org.name}"?`);
                  setConfirmationButtonType(false);
                  setShowAlert(true);
                }}
                title="Regenerate Token"
              >
                Regenerate Token
              </button>
            </div>
          </li>
        ))}
      </ul>

      <AlertBox
        show={showAlert}
        message={alertMessage}
        onClose={confirmationButtonType ? handleConfirmation : handleAlertNo}
        onConfirm={!confirmationButtonType ? handleAlertYes : undefined}
        type={alertType}
      >
        {operationType === 'delete' && !confirmationButtonType && (
          <div>
            <p>Please type <strong>"Delete"</strong> to confirm:</p>
            <input
              type="text"
              value={deleteConfirmation}
              onChange={handleDeleteConfirmationChange}
              placeholder="Delete"
            />
          </div>
        )}
      </AlertBox>

      {/* Easter Egg Popup */}
      {showEasterEgg && (
        <div 
          className="easter-egg-overlay"
          onClick={() => setShowEasterEgg(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
        >
          <div 
            className="easter-egg-popup"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              padding: '40px',
              borderRadius: '10px',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              margin: '20px'
            }}
          >
            <h2 style={{ 
              color: '#333', 
              marginBottom: '20px',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
              🎉  🎉
            </h2>
            <p style={{ 
              color: '#666', 
              fontSize: '18px',
              marginBottom: '20px'
            }}>
              Brandon was here
            </p>
            <button
              onClick={() => setShowEasterEgg(false)}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
